import React, { useState } from "react"

//Types
import DragAndUploadFileModule, { CSVFormatFile } from "@/UI/Inputs/DragAndUploadFileModule"
import { Button, Modal, Progress, UploadFile, message } from "antd"

import { ErrorNotification } from "@/UI/Notifications/NotificationTemplate.component"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { useTranslation } from "react-i18next"
import { CreateClientPayload } from "@/types/clients.types"
import { useClients } from "@/hooks/useClients"
import { postStore } from "@/api/stores"
import { StopType } from "@/types/orders.types"
import { useGeocoding } from "@/hooks/useGeocoding"
import { getReverseAddress } from "@/modules/shared/UploadTemplate/upload_utils"
import { useUser } from "@/hooks/useUser"
import { TFunction } from "i18next"
import { usePapaParse } from "react-papaparse"
interface ClientRow {
  [key: string]: string
  name: string
  email: string
  phone: string
  location: string
  lat: string
  lng: string
  "timeAtStop(m)": string
  arrivalTimeStart: string
  arrivalTimeEnd: string
}

const convertClientRowToRequest = (t: TFunction, row: ClientRow): CreateClientPayload => {
  const client: CreateClientPayload = {
    name: row.name,
    phone: row.phone,
    email: row.email,
    timeAtStop: parseInt(row["timeAtStop(m)"]) || 0,
    arrivalTimes: {
      start: /^\d{2}:\d{2}$/.test(row.arrivalTimeStart) ? row.arrivalTimeStart : "",
      end: /^\d{2}:\d{2}$/.test(row.arrivalTimeEnd) ? row.arrivalTimeEnd : "",
    },
  }

  // Check if arrivalTimeStart is after arrivalTimeEnd
  if (client.arrivalTimes.start && client.arrivalTimes.end) {
    const [startHour, startMinute] = client.arrivalTimes.start.split(":").map(Number)
    const [endHour, endMinute] = client.arrivalTimes.end.split(":").map(Number)
    if (
      startHour === undefined ||
      startMinute === undefined ||
      endHour === undefined ||
      endMinute === undefined
    ) {
      throw new Error(t("errors.invalidTimeFormat", { clientName: client.name }))
    }
    const startTime = new Date().setHours(startHour, startMinute)
    const endTime = new Date().setHours(endHour, endMinute)

    if (startTime > endTime) {
      throw new Error(t("errors.startTimeAfterEndTime", { clientName: client.name }))
    }
  }

  return client
}

const ClientsUpload: React.FC<{
  open: boolean
  onClose: () => void
}> = ({ open, onClose }) => {
  const { t } = useTranslation()
  const { readString } = usePapaParse()
  const [file, setFile] = useState([] as UploadFile[])
  const [isLoading, setIsLoading] = useState(false)
  const [progressPercent, setProgressPercent] = useState(0)

  const { createClient, fetchClients } = useClients()
  const { fetchStores } = useUser()
  const { geocodeByAddress } = useGeocoding()

  const convertFiletoJson = async () => {
    if (!file || !file[0]) {
      return
    }
    const reader = new FileReader()
    reader.readAsText(file[0].originFileObj as Blob)
    return new Promise<ClientRow[]>((resolve, reject) => {
      reader.onload = (e) => {
        const csv = e.target?.result
        const rows = (csv as string).split("\n") as string[]
        if (rows === undefined || rows.length < 2) {
          reject()
        }

        let clientRows: ClientRow[] = []
        readString(csv as string, {
          // Headers contained in results?.meta?.fields
          header: true,
          complete: (results) => {
            clientRows = results?.data as ClientRow[]
          },
        })

        resolve(clientRows as ClientRow[])
      }
      reader.onerror = (e) => {
        reject(e)
      }
    })
  }

  const processTemplateFile = async () => {
    try {
      setIsLoading(true)
      setProgressPercent(0)
      const submittedClients: ClientRow[] | undefined = await convertFiletoJson()

      if (!submittedClients) {
        setIsLoading(false)
        return
      }

      if (submittedClients?.length === 0) {
        throw new Error(t("errors.clientUpload.noClientsFound"))
      }

      let finishedClients = 0
      for (const client of submittedClients) {
        const requestClient = convertClientRowToRequest(t, client)

        if (client.location !== "" || client.lat !== "" || client.lng !== "") {
          let address = ""
          let postalCode = ""
          let lat = 0
          let lng = 0
          if (client.lat === "" || client.lng === "") {
            const geocodingResult = await geocodeByAddress(client.location)
            lat = geocodingResult?.location.lat ?? 0
            lng = geocodingResult?.location.lng ?? 0
            postalCode = geocodingResult?.address.postalCode ?? ""
            address = client.location
          } else if (client.location === "") {
            lat = parseFloat(client.lat)
            lng = parseFloat(client.lng)
            address = await getReverseAddress(lat, lng)
          } else {
            lat = parseFloat(client.lat)
            lng = parseFloat(client.lng)
            address = client.location
          }

          const { data } = await postStore({
            alias: client.name,
            address: address,
            name: client.name,
            phone: client.phone,
            email: client.email,
            postalCode: postalCode,
            latitude: lat,
            longitude: lng,
            type: StopType.PICKUP,
          })
          requestClient.locationIds = data.id ? [data.id] : []
        }

        if (!requestClient) {
          setIsLoading(false)
          return
        }

        await createClient(requestClient)
        finishedClients++
        setProgressPercent(Math.round((finishedClients / submittedClients.length) * 100))
      }

      await fetchClients(true)
      await fetchStores()
      setFile([])
      setIsLoading(false)
    } catch (err: any) {
      setIsLoading(false)
      setFile([])
      throw err
    }
  }
  const handleFileError = (title: string, error: any) => {
    // To translate
    ErrorNotification(
      title,
      <>
        <p>{error.message}</p>
        <p>{t("errors.draftUpload.reUploadError")}</p>
      </>
    )
    console.log(error)
  }

  const handleNext = async () => {
    try {
      await processTemplateFile()
      onClose()
      message.success(t("common.clientUpload.uploadSuccessMessage"))
    } catch (err: any) {
      handleFileError(t("errors.draftUpload.processTemplate"), err)
      onClose()
      setFile([])
      setIsLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      title={t("common.importFile")}
      destroyOnClose={true}
      onCancel={() => {
        setIsLoading(false)
        setFile([])
        onClose()
      }}
      footer={[
        <div className="flex flex-col">
          <div className="flex justify-end ">
            <Button
              className=""
              onClick={() => {
                setIsLoading(false)
                setFile([])
                onClose()
              }}
            >
              {t("common.cancel")}
            </Button>
            <KosmoButton
              id="draft-order-upload-next-step"
              classname="ml-2"
              isDisabled={!file.length}
              isLoading={isLoading}
              onClick={handleNext}
              type="primary"
            >
              {t("new-orders.DraftOrderUpload.uploadAndPreview")}
            </KosmoButton>
          </div>
          {isLoading && (
            <div className="flex mt-2">
              <Progress percent={progressPercent} strokeColor={"#4100FF"} />
            </div>
          )}
        </div>,
      ]}
    >
      <div className={`flex flex-col w-full space-y-4 items-center`}>
        <DragAndUploadFileModule
          file={file}
          setFile={(file) => {
            setFile(file)
          }}
          isDisabled={isLoading}
          supportedFileTypes={[CSVFormatFile]}
          hint={t("clients.clientsUpload.uploadMaxSize")}
        />
      </div>
    </Modal>
  )
}
export default ClientsUpload
